import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useAdmin } from '../../context/AdminContext';
import { 
  Users, 
  Store, 
  BarChart3, 
  TrendingUp,
  Activity,
  DollarSign
} from 'lucide-react';
import Card from '../../components/common/Card';

// ============== Small Sparkline Components =================
function RevenueSparkline({ stats }) {
  const { t } = useTranslation();
  const series = stats?.monthlyRevenueSeries || {};
  const entries = Object.entries(series);
  const values = entries.map(e => e[1]);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const points = values.map((v,i) => {
    const x = (i / (values.length - 1 || 1)) * 100;
    const y = 60 - ((v - min) / range) * 50; // chart height 60
    return `${x},${y}`;
  }).join(' ');
  return (
    <div className="h-48 md:h-64 flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        {values.length > 1 ? (
          <svg viewBox="0 0 100 60" className="w-full h-full">
            <polyline fill="none" stroke="#f59e0b" strokeWidth="2" points={points} strokeLinejoin="round" strokeLinecap="round" />
            {values.map((v,i) => {
              const x = (i / (values.length - 1 || 1)) * 100;
              const y = 60 - ((v - min) / range) * 50;
              return <circle key={i} cx={x} cy={y} r={1.5} fill="#f59e0b" />;
            })}
          </svg>
        ) : (
          <p className="text-gray-500 text-sm">{t('admin.noRevenueData')}</p>
        )}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-400">
        {entries.slice(-3).map(([month,val]) => (
          <div key={month} className="flex flex-col">
            <span className="font-medium text-gray-300">${val?.toFixed(2) || '0.00'}</span>
            <span>{month}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function UserGrowthSparkline({ stats }) {
  const { t } = useTranslation();
  const series = stats?.monthlyUserSignupSeries || {};
  const entries = Object.entries(series);
  const values = entries.map(e => e[1]);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const points = values.map((v,i) => {
    const x = (i / (values.length - 1 || 1)) * 100;
    const y = 60 - ((v - min) / range) * 50;
    return `${x},${y}`;
  }).join(' ');
  return (
    <div className="h-48 md:h-64 flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        {values.length > 1 ? (
          <svg viewBox="0 0 100 60" className="w-full h-full">
            <polyline fill="none" stroke="#34d399" strokeWidth="2" points={points} strokeLinejoin="round" strokeLinecap="round" />
            {values.map((v,i) => {
              const x = (i / (values.length - 1 || 1)) * 100;
              const y = 60 - ((v - min) / range) * 50;
              return <circle key={i} cx={x} cy={y} r={1.5} fill="#34d399" />;
            })}
          </svg>
        ) : (
          <p className="text-gray-500 text-sm">{t('admin.noUserData')}</p>
        )}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-400">
        {entries.slice(-3).map(([month,val]) => (
          <div key={month} className="flex flex-col">
            <span className="font-medium text-gray-300">{val}</span>
            <span>{month}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrderSparkline({ stats }) {
  const { t } = useTranslation();
  const series = stats?.monthlyOrderSeries || {};
  const entries = Object.entries(series);
  const values = entries.map(e => e[1]);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const points = values.map((v,i) => {
    const x = (i / (values.length - 1 || 1)) * 100;
    const y = 60 - ((v - min) / range) * 50;
    return `${x},${y}`;
  }).join(' ');
  return (
    <div className="h-40 md:h-48 flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        {values.length > 1 && values.some(v=>v>0) ? (
          <svg viewBox="0 0 100 60" className="w-full h-full">
            <polyline fill="none" stroke="#8b5cf6" strokeWidth="2" points={points} strokeLinejoin="round" strokeLinecap="round" />
            {values.map((v,i) => {
              const x = (i / (values.length - 1 || 1)) * 100;
              const y = 60 - ((v - min) / range) * 50;
              return <circle key={i} cx={x} cy={y} r={1.5} fill="#8b5cf6" />;
            })}
          </svg>
        ) : (
          <p className="text-gray-500 text-sm">{t('admin.noOrderData')}</p>
        )}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-400">
        {entries.slice(-3).map(([month,val]) => (
          <div key={month} className="flex flex-col">
            <span className="font-medium text-gray-300">{val}</span>
            <span>{month}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { 
    stats, 
    loading, 
    fetchStats,
    fetchUsers,
    fetchShops,
    fetchOrders,
    recentUsers,
    recentShops,
    recentOrders
  } = useAdmin();

  useEffect(() => {
    fetchStats();
    // Load minimal recent lists (first page small limit)
    fetchUsers(1, 10, {});
    fetchShops(1, 10, {});
    fetchOrders(1, 10, {});
  }, [fetchStats, fetchUsers, fetchShops, fetchOrders]);

  const numberFmt = useMemo(() => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }), []);
  const currencyFmt = useMemo(() => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }), []);

  const formatGrowth = (val, prevBase) => {
    if (val === null || val === undefined) return '0%';
    if ((prevBase === 0 || prevBase === undefined) && val > 0) return t('admin.growthNew');
    const sign = val > 0 ? '+' : '';
    return `${sign}${val.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">{t('admin.loadingAdminDashboard')}</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: t('admin.totalUsers'),
      value: numberFmt.format(stats?.totalUsers || 0),
      icon: Users,
      color: 'bg-blue-600',
      change: stats?.userGrowth || 0,
      prevBase: stats?.prevMonthNewUsers,
      changeType: 'users'
    },
    {
      title: t('admin.activeShops'),
      value: numberFmt.format(stats?.activeShops || 0),
      icon: Store,
      color: 'bg-green-600',
      change: stats?.shopGrowth || 0,
      prevBase: 0,
      changeType: 'shops'
    },
    {
      title: t('admin.monthlyRevenue'),
      value: currencyFmt.format(stats?.monthlyRevenue || 0),
      icon: DollarSign,
      color: 'bg-amber-600',
      change: stats?.revenueGrowth || 0,
      prevBase: stats?.prevMonthRevenue,
      changeType: 'revenue'
    },
    {
      title: t('admin.totalOrders'),
      value: numberFmt.format(stats?.totalOrders || 0),
      icon: Activity,
      color: 'bg-purple-600',
      change: stats?.orderGrowth || 0,
      prevBase: stats?.prevMonthOrders,
      changeType: 'orders'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{t('admin.dashboard')}</h1>
          <p className="text-gray-400 text-sm md:text-base">{t('admin.welcomeBack')}, {user?.firstName}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          {statCards.map((stat, index) => (
            <Card key={index} className="hover:border-gray-600/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs md:text-sm text-gray-400 mb-1">{stat.title}</p>
                  <p className="text-xl md:text-2xl font-bold text-white">{stat.value}</p>
                  <div className="flex items-center mt-2">
                    <TrendingUp className={`w-3 h-3 md:w-4 md:h-4 mr-1 ${stat.change > 0 ? 'text-green-400' : stat.change < 0 ? 'text-red-400 rotate-180' : 'text-gray-400'}`} />
                    <span className={`text-xs md:text-sm ${stat.change > 0 ? 'text-green-400' : stat.change < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                      {formatGrowth(stat.change, stat.prevBase)} {t('admin.thisMonth')}
                    </span>
                  </div>
                </div>
                <div className={`p-2 md:p-3 rounded-lg ${stat.color} flex-shrink-0`}>
                  <stat.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Charts and Recent Activity */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 md:gap-6 mb-8">
          {/* Revenue Chart (Sparkline) */}
          <Card className="col-span-1">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base md:text-lg font-semibold text-white">{t('admin.revenueOverview')}</h3>
              <BarChart3 className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
            </div>
            <RevenueSparkline stats={stats} />
          </Card>

          {/* User Growth Chart (Sparkline) */}
            <Card className="col-span-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base md:text-lg font-semibold text-white">{t('admin.userGrowth')}</h3>
                <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
              </div>
              <UserGrowthSparkline stats={stats} />
            </Card>
            {/* Order Sparkline */}
            <Card className="col-span-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base md:text-lg font-semibold text-white">{t('admin.totalOrders')}</h3>
                <Activity className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
              </div>
              <OrderSparkline stats={stats} />
            </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Recent Users */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base md:text-lg font-semibold text-white">{t('admin.recentUsers')}</h3>
              <Users className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
            </div>
            <div className="space-y-3">
              {recentUsers?.slice(0, 5).map((user) => (
                <div key={user.id} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm md:text-base truncate">{user.firstName} {user.lastName}</p>
                    <p className="text-xs md:text-sm text-gray-400 truncate">{user.email}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className="text-xs md:text-sm text-gray-400">{user.role}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Recent Shops */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base md:text-lg font-semibold text-white">{t('admin.recentShops')}</h3>
              <Store className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
            </div>
            <div className="space-y-3">
              {recentShops?.slice(0, 5).map((shop) => (
                <div key={shop.id} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm md:text-base truncate">{shop.name}</p>
                    <p className="text-xs md:text-sm text-gray-400 truncate">@{shop.slug}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className="text-xs md:text-sm text-gray-400">{shop.status}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(shop.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Recent Orders */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base md:text-lg font-semibold text-white">{t('admin.recentOrders')}</h3>
              <Activity className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
            </div>
            <div className="space-y-3">
              {recentOrders?.slice(0, 5).map((order) => (
                <div key={order.id} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm md:text-base">Order #{order.id}</p>
                    <p className="text-xs md:text-sm text-gray-400 truncate">{order.shopName}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className="text-xs md:text-sm text-gray-400">${order.totalAmount}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
